import React, { useState, useRef, useEffect } from 'react';
import PropTypes from 'prop-types';
import styled from '@grebban/style-system-react';
import { ProductCard } from 'components/product/ProductCard';
import { above } from '@fogia/core-web/utils/mediaqueries';
import { isEmpty } from '@fogia/core-web/utils/object';
import loadable from '@loadable/component';
import FilterBar from 'components/Filter/FilterBar';
import { Transition } from 'react-transition-group';
import useBreakpoint from '@fogia/core-web/hooks/useBreakpoint';
import { allowScroll, preventScroll } from '@fogia/core-web/utils/scroll';
import { inServer } from '@fogia/core-web/constants';
import CmsModules from '@fogia/core-web/libs/sanity/content/CmsModules';
import NoResults from './NoResults';

const Filter = loadable(() => import('components/Filter'));

const Wrapper = styled('div')`
    padding: 0 var(--grid-gap);
`;

const CardGrid = styled('div')`
    --grid-cols: 2;
    --grid-col-gap: 12px;

    container-type: inline-size;
    padding: 24px 12px;
    padding-top: 0;
    display: grid;
    grid-template-columns: repeat(2, 1fr);
    gap: 24px var(--grid-col-gap);
    width: 100%;

    /*
     * Image container height for product cards. Uses one column width (100cqw = container width
     * per grid cell) so that wide cards (span 2) get the same image height as single-column cards.
     * Formula: (single-col-width) × 5/4 for a 4:5 aspect ratio.
     */
    & > * {
        --product-image-height: calc(
            (100cqw - (var(--grid-cols) - 1) * var(--grid-col-gap)) / var(--grid-cols) * 5 / 4
        );
    }

    & > .wide-product-card {
        grid-column: span 2;
    }

    ${above['desktop.sm']} {
        --grid-cols: 4;
        --grid-col-gap: 10px;
        grid-template-columns: repeat(var(--grid-cols), 1fr);
        gap: 30px var(--grid-col-gap);
        padding: 6px 0;
    }
`;

const CmsModuleFirst = styled('div')`
    grid-column: 1 / -1;
    grid-row: 6;
    aspect-ratio: 4 / 5;
    overflow: hidden;

    ${above['desktop.sm']} {
        grid-row: 3;
        grid-column: 3 / 5;
    }
`;

const CmsModuleSecond = styled('div')`
    grid-column: 1 / -1;
    grid-row: 11;
    aspect-ratio: 4 / 5;
    overflow: hidden;

    ${above['desktop.sm']} {
        grid-row: 6;
        grid-column: 1 / 3;
    }
`;

const GridWrapper = styled('div')`
    display: flex;
    width: 100%;
    flex-direction: row;
`;

const DesktopFilterWrapper = styled('div')`
    padding-left: 10px;

    ${above['tablet.lg']} {
        padding-left: 2px;
    }

    ${above['desktop.sm']} {
        padding-top: 0px;
        padding-right: -10px;
    }
`;

const CategoryModule = ({ config, response, hasCategoryFilter, hideFilterBar, pageContent }) => {
    const isMobile = useBreakpoint({ to: 'desktop.sm' });
    const headerHeight =
        (!inServer && getComputedStyle(document?.documentElement).getPropertyValue('--header-height')) || 0;

    const [filterVisible, setFilterVisible] = useState(false);
    const [shouldScroll, setShouldScroll] = useState(false);

    const wrapperRef = useRef({});
    const filterRef = useRef();

    const { clear, filters, hitsCount, selectedFilters = [], sorts = [], handleChange = {}, products } = response;

    const scrollToTop = (next, smooth = false) => {
        // Scroll back to top of wrapper on pagination
        if (wrapperRef && wrapperRef.current) {
            scrollTo({
                smooth,
                top: wrapperRef.current.offsetTop - headerHeight,
            });
        }
    };

    const onFilterChange = () => {
        setShouldScroll(true);
        scrollToTop(true);
    };

    const filterOverlayData = {
        filters,
        handleChange: handleChange.filter,
        scrollToTop: onFilterChange,
        clear,
        hitsCount,
        config,
        selectedFilters,
        hasCategoryFilter,
        setFilterVisible,
    };

    useEffect(() => {
        if (filterVisible && isMobile) {
            preventScroll();
        } else {
            allowScroll();
        }
    }, [filterVisible, isMobile]);

    return (
        <Wrapper ref={wrapperRef}>
            {!isEmpty(response) && (
                <>
                    {!hideFilterBar && (
                        <FilterBar
                            filters={filters}
                            filterOverlayData={filterOverlayData}
                            shouldScrollState={[shouldScroll, setShouldScroll]}
                            filterState={[filterVisible, setFilterVisible]}
                            scrollToTop={onFilterChange}
                            handleChange={handleChange}
                            config={config}
                            hasCategoryFilter={hasCategoryFilter}
                            selectedFilters={selectedFilters}
                            clear={clear}
                            isMobile={filterVisible && isMobile}
                            setFilterVisible={setFilterVisible}
                            hitsCount={hitsCount}
                            sorts={sorts}
                        />
                    )}

                    <GridWrapper ref={filterRef}>
                        {filterVisible && !isMobile && (
                            <DesktopFilterWrapper>
                                <Transition in={filterVisible} timeout={0}>
                                    {(state) => (
                                        <Filter
                                            className={state}
                                            filters={filters}
                                            scrollToTop={onFilterChange}
                                            handleChange={handleChange.filter}
                                            config={config}
                                            hasCategoryFilter={hasCategoryFilter}
                                            selectedFilters={selectedFilters}
                                            clear={clear}
                                            isMobile={filterVisible && !isMobile}
                                            setFilterVisible={setFilterVisible}
                                            hitsCount={hitsCount}
                                        />
                                    )}
                                </Transition>
                            </DesktopFilterWrapper>
                        )}

                        {hitsCount === 0 && !hideFilterBar ? (
                            <NoResults />
                        ) : (
                            <CardGrid>
                                {products &&
                                    products.map((product) => (
                                        <ProductCard product={product} key={product.productId} />
                                    ))}
                                {pageContent?.[0] && (
                                    <CmsModuleFirst>
                                        <CmsModules data={[pageContent[0]]} />
                                    </CmsModuleFirst>
                                )}
                                {pageContent?.[1] && (
                                    <CmsModuleSecond>
                                        <CmsModules data={[pageContent[1]]} />
                                    </CmsModuleSecond>
                                )}
                            </CardGrid>
                        )}
                    </GridWrapper>
                </>
            )}
        </Wrapper>
    );
};

CategoryModule.propTypes = {
    config: PropTypes.object.isRequired,
    hasCategoryFilter: PropTypes.bool,
    response: PropTypes.shape({
        clear: PropTypes.object,
        filters: PropTypes.arrayOf(PropTypes.object),
        handleChange: PropTypes.object,
        hasMore: PropTypes.bool,
        hitsCount: PropTypes.number,
        offset: PropTypes.number,
        page: PropTypes.number,
        pageCount: PropTypes.number,
        pageSize: PropTypes.number,
        productsCount: PropTypes.number,
        products: PropTypes.array,
        selectedFilters: PropTypes.arrayOf(PropTypes.object),
        sorts: PropTypes.arrayOf(
            PropTypes.exact({ index: PropTypes.string, label: PropTypes.string, selected: PropTypes.bool }),
        ),
    }).isRequired,
    filterButtons: PropTypes.array,
    hideFilterBar: PropTypes.bool,
    pageContent: PropTypes.array,
};

CategoryModule.defaultProps = {
    hasCategoryFilter: false,
    filterButtons: [],
    hideFilterBar: false,
    pageContent: undefined,
};

export default CategoryModule;
